import {
  createDrawerNavigator,
  DrawerContentScrollView,
  DrawerItemList,
} from "@react-navigation/drawer";
import {
  AuthorizedAppRootScreenParamList,
  ValidSetupRootParamList,
} from "../../../types";
import { SaleController } from "../../point-of-sale/components/SaleController";
import { View } from "react-native";
import { CurrentUserCard } from "../../../components/CurrentUserCard";
import { vendorCanAccessFeature } from "../../../utilities/feature-flags";
import { AccountFeatureFlag } from "@towersystems/roam-common/lib/generated-types";
import { Colors, Spacings } from "react-native-ui-lib";
import {
  Button,
  Divider,
  useWithAppState,
} from "@towersystems/roam-common-native";
import {
  AntDesign,
  FontAwesome5,
  Ionicons,
  MaterialCommunityIcons,
  MaterialIcons,
} from "@expo/vector-icons";
import { useTranslation } from "react-i18next";
import {
  useAuthenticationProvider,
  useMeProvider,
} from "@towersystems/roam-common-native/lib/utilities/authentication";
import { useCurrentVendorProvider } from "../../../utilities/vendor";
import { Text } from "@towersystems/roam-common-native";
import { ShiftStatusBadge } from "../../../components/ShiftStatusBadge";
import { RegisterRootScreen } from "../../point-of-sale/screens";
import { PendingSalesCountChip } from "../../../components/PendingSalesCountChip";
import { SalesRootScreen } from "../../sales/screens/SalesRootScreen";
import { StockCountsRootScreen } from "../../stock-counts/screens";
import { ShiftRootScreen } from "../../shift/screen/ShiftRootScreen";
import { PendingInvoiceArrivalsCountChip } from "../../../components/PendingInvoiceArrivalsCountChip";
import { InvoiceArrivalsRootScreen } from "../../invoice-arrival/screens/InvoiceArrivalsRootScreen/InvoiceArrivalsRootScreen";
import { PendingStockOrdersCountChip } from "../../../components/PendingStockOrdersChip";
import { StockOrderRootScreen } from "../../stock-order/screens/StockOrderRootScreen";
import { ProductsRootScreen } from "../../products/screens";
import { CustomersRootScreen } from "../../customer/screens";
import { SettingsRootScreen } from "../../settings/screens/SettingsRootScreen";
import {
  InvoiceSequenceContext,
  useInvoiceSequence,
} from "../../../utilities/invoice-sequence";
import React from "react";
import { NativeStackScreenProps } from "@react-navigation/native-stack";
import { LlmQuickWinsProvider } from "../../../utilities/llm-quick-wins/context";

const drawerStyles = {
  fontSize: 17,
  borderRadius: 10,
  fontFamily: "Gilmer-Medium",
};

const commonIconProps = {
  size: 16,
} as any;

const commonIconWrapperProps = {
  style: {
    justifyContent: "center",
    alignItems: "center",
    width: 30,
    height: 30,
  },
} as any;

const Drawer = createDrawerNavigator<ValidSetupRootParamList>();

export type AppRootScreenProps = NativeStackScreenProps<
  AuthorizedAppRootScreenParamList,
  "AppRootScreen"
>;

export const AppRootScreen = ({ navigation }: AppRootScreenProps) => {
  const { t } = useTranslation("navigation");
  const {
    appState: { locationId, terminalId },
  } = useWithAppState();
  const { currentVendor } = useCurrentVendorProvider();
  const { me } = useMeProvider();
  const invoiceSequenceContext = useInvoiceSequence();
  const authentication = useAuthenticationProvider();

  const currentUserOwner = Boolean(
    me.roles.find((i) => i.code === "store-owner"),
  );

  React.useEffect(() => {
    if (!locationId || !terminalId) {
      const id = setTimeout(() => {
        navigation.reset({
          index: 0,
          routes: [{ name: "SetupRootScreen" }],
        });
      }, 100);
      return () => clearTimeout(id);
    }
  }, [navigation, locationId, terminalId]);

  React.useEffect(() => {
    invoiceSequenceContext.methods
      .sync()
      .catch((err) => console.log("FAILED TO SYNC INVOICE SEQUENCES", err));
  }, [invoiceSequenceContext.methods.sync]);

  if (!locationId || !terminalId) {
    return null; // user will be redirected to SetupRootScreen
  }

  return (
    <InvoiceSequenceContext.Provider value={invoiceSequenceContext}>
      <LlmQuickWinsProvider>
        <SaleController>
          <Drawer.Navigator
            useLegacyImplementation={false}
            drawerContent={(props) => {
              return (
                <DrawerContentScrollView
                  contentContainerStyle={{
                    justifyContent: "space-between",
                    minHeight: "100%",
                  }}
                >
                  <View style={{ padding: Spacings.s4 }}>
                    <CurrentUserCard navigation={props.navigation} />
                    <Divider marginBottom={0} />
                  </View>
                  <View style={{ flex: 1 }}>
                    <DrawerItemList {...props} />
                  </View>

                  <View
                    style={{
                      padding: Spacings.s4,
                    }}
                  >
                    <Button
                      label={t("logout")}
                      size="compact"
                      variant="outline"
                      onPress={() => authentication.logout()}
                      icon={
                        <MaterialCommunityIcons
                          name="logout-variant"
                          size={20}
                          color="#000000070"
                        />
                      }
                    ></Button>
                  </View>
                </DrawerContentScrollView>
              );
            }}
            id="LeftDrawer"
            screenOptions={{
              drawerType: "front",
              unmountOnBlur: true,
              drawerStyle: {
                backgroundColor: Colors.surface,
              },
              headerShown: false,
              sceneContainerStyle: {
                overflow: "hidden",
                backgroundColor: Colors.surface,
                height: "100%",
              },
              drawerLabelStyle: [drawerStyles],
              drawerActiveBackgroundColor: "#EBEBEB",
              drawerInactiveTintColor: Colors.text,
            }}
          >
            <Drawer.Screen
              options={{
                drawerLabel: ({ color, focused }) => {
                  return (
                    <View>
                      <Text style={{ ...drawerStyles, color }}>
                        {t("logged_in.pos.label")}
                      </Text>
                      <View style={{ position: "absolute", right: -25 }}>
                        <ShiftStatusBadge />
                      </View>
                    </View>
                  );
                },

                drawerIcon: ({ color }) => (
                  <View {...commonIconWrapperProps}>
                    <MaterialIcons
                      color={color}
                      name="point-of-sale"
                      {...commonIconProps}
                    />
                  </View>
                ),
              }}
              name="RegisterRootScreen"
              component={RegisterRootScreen}
            />
            <Drawer.Screen
              name="SalesRootScreen"
              options={{
                headerShown: false,
                drawerLabel: ({ color, focused }) => {
                  return (
                    <View>
                      <Text style={{ ...drawerStyles, color }}>
                        {t("logged_in.sales.label")}
                      </Text>
                      <View style={{ position: "absolute", right: -25 }}>
                        <PendingSalesCountChip />
                      </View>
                    </View>
                  );
                },
                drawerIcon: ({ color }) => (
                  <View {...commonIconWrapperProps}>
                    <FontAwesome5
                      color={color}
                      name="file-invoice"
                      {...commonIconProps}
                    />
                  </View>
                ),
              }}
              component={SalesRootScreen}
            />
            {vendorCanAccessFeature(
              AccountFeatureFlag.POS_APP_STOCK_COUNTS_UI,
              currentVendor,
            ) && (
              <Drawer.Screen
                options={{
                  drawerLabel: ({ color, focused }) => {
                    return (
                      <View>
                        <Text style={{ ...drawerStyles, color }}>
                          {t("logged_in.stock_counts.label")}
                        </Text>
                      </View>
                    );
                  },
                  drawerIcon: ({ color }) => (
                    <View {...commonIconWrapperProps}>
                      <MaterialCommunityIcons
                        name="clipboard-check-outline"
                        {...commonIconProps}
                        color={color}
                      />
                    </View>
                  ),
                }}
                name="StockCountsRootScreen"
                component={StockCountsRootScreen}
              />
            )}
            <Drawer.Screen
              name="ShiftRootScreen"
              options={{
                headerShown: false,
                drawerLabel: t("logged_in.shift.label"),

                drawerIcon: ({ color }) => (
                  <View {...commonIconWrapperProps}>
                    <MaterialCommunityIcons
                      name="account-clock-outline"
                      {...commonIconProps}
                      color={color}
                    />
                  </View>
                ),
              }}
              component={ShiftRootScreen}
            />
            {vendorCanAccessFeature(
              AccountFeatureFlag.POS_APP_INVOICE_ARRIVALS_UI,
              currentVendor,
            ) && (
              <Drawer.Screen
                name="InvoiceArrivalsRootScreen"
                options={{
                  headerShown: false,

                  drawerLabel: ({ color }) => {
                    return (
                      <View>
                        <Text style={{ ...drawerStyles, color }}>
                          {t("logged_in.invoice_arrival.label")}
                        </Text>
                        <View style={{ position: "absolute", right: -25 }}>
                          <PendingInvoiceArrivalsCountChip />
                        </View>
                      </View>
                    );
                  },
                  drawerIcon: ({ color }) => (
                    <View {...commonIconWrapperProps}>
                      <MaterialCommunityIcons
                        name="truck-delivery-outline"
                        {...commonIconProps}
                        color={color}
                      />
                    </View>
                  ),
                }}
                component={InvoiceArrivalsRootScreen}
              />
            )}
            {vendorCanAccessFeature(
              AccountFeatureFlag.POS_APP_STOCK_ORDERS_UI,
              currentVendor,
            ) && (
              <Drawer.Screen
                name="StockOrderRootScreen"
                options={{
                  headerShown: false,
                  drawerLabel: ({ color, focused }) => {
                    return (
                      <View>
                        <Text style={{ ...drawerStyles, color }}>
                          {t("logged_in.stock_orders.label")}
                        </Text>
                        <View style={{ position: "absolute", right: -25 }}>
                          <PendingStockOrdersCountChip />
                        </View>
                      </View>
                    );
                  },

                  drawerIcon: ({ color }) => (
                    <View {...commonIconWrapperProps}>
                      <MaterialCommunityIcons
                        name="cart-outline"
                        {...commonIconProps}
                        color={color}
                      />
                    </View>
                  ),
                }}
                component={StockOrderRootScreen}
              />
            )}

            {vendorCanAccessFeature(
              AccountFeatureFlag.POS_APP_MANAGE_PRODUCTS_UI,
              currentVendor,
            ) &&
              currentUserOwner && (
                <Drawer.Screen
                  name="ProductsRootScreen"
                  options={{
                    headerShown: false,
                    drawerLabel: t("logged_in.product.label"),

                    drawerIcon: ({ color }) => (
                      <View {...commonIconWrapperProps}>
                        <MaterialCommunityIcons
                          color={color}
                          name="barcode"
                          {...commonIconProps}
                        />
                      </View>
                    ),
                  }}
                  component={ProductsRootScreen}
                />
              )}

            {vendorCanAccessFeature(
              AccountFeatureFlag.POS_APP_MANAGE_CUSTOMERS_UI,
              currentVendor,
            ) &&
              currentUserOwner && (
                <Drawer.Screen
                  name="CustomersRootScreen"
                  options={{
                    headerShown: false,
                    drawerLabel: t("logged_in.customer.label"),
                    drawerIcon: ({ color }) => (
                      <View {...commonIconWrapperProps}>
                        <AntDesign
                          color={color}
                          name="team"
                          {...commonIconProps}
                        />
                      </View>
                    ),
                  }}
                  component={CustomersRootScreen}
                />
              )}

            <Drawer.Screen
              name="SettingsRootScreen"
              options={{
                headerShown: false,
                drawerLabel: t("logged_in.settings.label"),
                drawerAllowFontScaling: true,
                drawerIcon: ({ color }) => (
                  <View {...commonIconWrapperProps}>
                    <Ionicons
                      color={color}
                      name="settings-outline"
                      {...commonIconProps}
                    />
                  </View>
                ),
              }}
              component={SettingsRootScreen}
            />
          </Drawer.Navigator>
        </SaleController>
      </LlmQuickWinsProvider>
    </InvoiceSequenceContext.Provider>
  );
};
